import { Component, EventEmitter, Inject, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';

import { MainFacade } from '../../main.facade';
import { resolveTestMode } from '../../../shared/test-mode';
import { VISUALIZER, IVisualizer, VisualizerComponent } from '@jax-data-science/sci-image-visualizer';
import { SpatialOmicsService } from '../../../services/spatial-omics.service';

/**
 * App-integration host for the plotting view. The pure plotting UI lives in
 * {@link VisualizerComponent} (the @jax-data-science/sci-image-visualizer library); this host embeds
 * `<visualizer>`, relays its stack/grayscale events, and supplies the
 * app-specific toolbar buttons (the processing pipeline) as projected content —
 * so the visualization/toolbar stay free of those dependencies. (The Region
 * Editor button lives in the library toolbar and opens a floating dialog.)
 */
@Component({
  selector: 'diagram',
  templateUrl: './diagram.component.html',
  styleUrls: ['./diagram.component.scss'],
})
export class DiagramComponent implements OnInit, OnDestroy {
  @Output() isStackEvent = new EventEmitter(false);
  @Output() isGrayscaleEvent = new EventEmitter(false);

  /** Processing pipeline dialog state. */
  showPipelineDialog = false;
  pipelineInputImage: any = null;
  pipelineFullImageSize: { width: number; height: number } | null = null;
  pipelineSourceRect: { x: number; y: number; width: number; height: number } | null = null;
  /** Physical pixel size (µm/px) of the original image, so the dialog viewer can
   *  draw a correct scale bar for the (possibly zoomed) crop. null when the image
   *  carries no physical metadata — the bar then stays hidden, as on the main viewer. */
  pipelineSourcePixelSize: { mppX: number | null; mppY: number | null } | null = null;

  /** Test mode: expose test-only plot modes (e.g. the napari `Image` mode) in
   *  the visualizer's selector. Activated by a `?test=1` URL param, then
   *  persisted so it survives reloads/auth redirects (see {@link resolveTestMode});
   *  off by default, so production users never see them. */
  readonly testMode = resolveTestMode();

  @ViewChild(VisualizerComponent) private visualizer?: VisualizerComponent;

  private fileName: string | undefined;
  private filenameSub?: Subscription;
  private plotTypeSub?: Subscription;

  constructor(private mainFacade: MainFacade, @Inject(VISUALIZER) private plotService: IVisualizer,
              private spatialOmics: SpatialOmicsService) {}

  ngOnInit(): void {
    this.filenameSub = this.mainFacade.getFilename().subscribe(name => {
      if (name) this.fileName = name;
    });
    // Through the component, not IVisualizer.setPlotType: only the component re-plots and
    // keeps its selector in step.
    this.plotTypeSub = this.spatialOmics.getPlotTypeRequest$().subscribe(type => {
      this.visualizer?.onSelectPlotType(type);
    });
  }

  ngOnDestroy(): void {
    this.filenameSub?.unsubscribe();
    this.plotTypeSub?.unsubscribe();
  }

  /** Open the processing pipeline dialog with the currently displayed pixels
   *  (including the active zoom), read back from the visualizer. */
  async openPipelineDialog(): Promise<void> {
    const pixelData = this.plotService.getDisplayedPixelData();
    if (pixelData) {
      const { ProcessingImage } = await import('../../models/processing-pipeline/processing-image');
      this.pipelineInputImage = new ProcessingImage(
        pixelData.width, pixelData.height, pixelData.channels, pixelData.data
      );
    }
    this.pipelineFullImageSize = this.plotService.getTrueImageSize();
    // Region of the original image the displayed (possibly zoomed/cropped) pixels
    // cover, so regions/masks drawn in the pipeline map back to original coords.
    this.pipelineSourceRect = this.plotService.getDisplayedSourceRect();
    // Physical pixel size of the original image (read synchronously from the
    // store-backed meta stream, as the router does for getTrueImageSize), so the
    // dialog can draw a scale bar; scaled for the crop in the preview component.
    let meta: any[] = [];
    this.plotService.getImageMeta().subscribe((m) => (meta = m)).unsubscribe();
    const m0 = meta?.[0];
    this.pipelineSourcePixelSize = m0
      ? { mppX: m0.mppX ?? null, mppY: m0.mppY ?? null }
      : null;
    this.showPipelineDialog = true;
  }

  /** Save the processed pipeline result as a PNG download. */
  savePipelineResult(blob: Blob): void {
    const filename = (this.fileName || 'processed') + '_pipeline.png';
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
  }
}
